import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CalendarPlusIcon, ChevronDownIcon, HeartIcon, SearchIcon, SendIcon, SlidersHorizontalIcon, UsersIcon } from 'lucide-react';
import { toast } from 'sonner';
import { SwipeDeck } from '../../components/swipe/SwipeDeck';
import { PerformerCardFace } from '../../components/swipe/PerformerCardFace';
import { PerformerDetail } from '../../components/PerformerDetail';
import { LineupBoard } from '../../components/organiser/LineupBoard';
import { OrganiserPreferencesForm } from '../../components/organiser/OrganiserPreferencesForm';
import { FeatureSheet } from '../../components/organiser/FeatureSheet';
import { SegmentedControl } from '../../components/ui/SegmentedControl';
import { EmptyState } from '../../components/ui/EmptyState';
import { Button } from '../../components/ui/Button';
import { ButtonLink } from '../../components/ui/ButtonLink';
import { Sheet } from '../../components/ui/Sheet';
import { useMarketplace } from '../../contexts/MarketplaceContext';
import { useIsDesktop } from '../../hooks/useMediaQuery';
import { travelCost } from '../../utils/event';
import { performerFit } from '../../utils/fit';
import { formatDate } from '../../utils/format';
import type { MusicEvent, Performer } from '../../types/marketplace';
type Tab = 'applicants' | 'discover' | 'lineup';
export function Review() {
  const {
    eventId
  } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const m = useMarketplace();
  const [sheetId, setSheetId] = useState<string | null>(null);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [featuring, setFeaturing] = useState<MusicEvent | null>(null);
  const myLive = m.events.filter(e => e.ownedByMe && e.status === 'live').sort((a, b) => a.date.localeCompare(b.date));
  const event = m.events.find(e => e.id === eventId);
  const tab = params.get('tab') as Tab || 'applicants';
  const setTab = (t: Tab) => setParams(t === 'applicants' ? {} : {
    tab: t
  }, {
    replace: true
  });
  useEffect(() => {
    if (!eventId && myLive[0]) navigate(`/o/review/${myLive[0].id}`, {
      replace: true
    });
  }, [eventId, myLive, navigate]);
  const {
    applicants,
    discover
  } = useMemo(() => {
    if (!event) return {
      applicants: [],
      discover: []
    };
    const key = (pid: string) => `${event.id}:${pid}`;
    const open = (p: Performer) => !m.hasMatch(event.id, p.id) && !m.orgPassed.includes(key(p.id)) && !event.slots.some(s => s.filledBy === p.id);
    const withFit = (list: Performer[]) => list.map(p => ({
      performer: p,
      fit: performerFit(p, event, m.organiserPrefs)
    })).sort((a, b) => b.fit.score - a.fit.score);
    return {
      applicants: withFit(m.performers.filter(p => event.applicantIds.includes(p.id) && open(p))),
      discover: withFit(m.performers.filter(p => !event.applicantIds.includes(p.id) && open(p) && !m.orgInvited.includes(key(p.id))))
    };
  }, [event, m]);
  if (!event) {
    return <div className="grid min-h-[70dvh] place-items-center" data-id="element-1069" data-mp="37tqh8kvelnnk">
        <EmptyState icon={<CalendarPlusIcon className="h-7 w-7" data-id="element-1071" data-mp="3mqhikzon19ft" />} title="No live events to review" body="Post an event to start receiving applicants." data-id="element-1070" data-mp="37vbc6blmr8ly">
          <ButtonLink to="/o/events/new" variant="accent" data-id="element-1072" data-mp="3hsg12j6v1i6g">
            Post an event
          </ButtonLink>
        </EmptyState>
      </div>;
  }
  const deck = tab === 'applicants' ? applicants : discover;
  const top = deck[0];
  const sheetItem = [...applicants, ...discover].find(x => x.performer.id === sheetId);
  const onSwipe = (p: Performer, dir: 'left' | 'right' | 'up') => {
    if (dir === 'left') return m.passPerformer(event.id, p.id);
    if (tab === 'applicants') m.likeApplicant(event.id, p.id);else {
      const quick = m.invitePerformer(event.id, p.id);
      toast(`Invite sent to ${p.name}`, {
        description: quick ? 'They’re online now — hang tight.' : 'You’ll match if they say yes.'
      });
    }
  };
  return <div className="flex min-h-[calc(100dvh-76px)] flex-col lg:min-h-[100dvh]" data-id="element-1073" data-mp="19irqnvvnzirp">
      <header className="px-5 pb-3 pt-6 lg:px-8 lg:pt-8" data-id="element-1074" data-mp="1dmzi40tibctm">
        <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-1075" data-mp="1il116lqxnc53">
          <div className="min-w-0" data-id="element-1076" data-mp="1nc97va0vojek">
            <label className="relative inline-flex max-w-full items-center" data-id="element-1077" data-mp="1ndatu9jfmqb2">
              <span className="sr-only" data-id="element-1078" data-mp="1sbvm9msg05oy">Event</span>
              <select value={event.id} onChange={e => navigate(`/o/review/${e.target.value}${tab !== 'applicants' ? `?tab=${tab}` : ''}`)} className="max-w-full appearance-none truncate rounded-sm bg-transparent py-1 pr-8 text-[26px] font-bold tracking-[-0.015em] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-1079" data-mp="1sb2ohbbgoqiy">
                {myLive.map(e => <option key={e.id} value={e.id} data-id="element-1080" data-mp="bwua440uhnie">
                    {e.name}
                  </option>)}
              </select>
              <ChevronDownIcon className="pointer-events-none absolute right-1 h-5 w-5 text-ink-2" data-id="element-1081" data-mp="4f5xm724ljud" />
            </label>
            <p className="text-sm text-ink-3" data-id="element-1082" data-mp="3vq5xsratv1ar">
              {formatDate(event.date)} · {event.slots.filter(s => !s.filledBy).length} open slots
            </p>
          </div>
          <div className="flex items-center gap-2" data-id="element-1083" data-mp="3lvnrrfhxutns">
            <SegmentedControl<Tab> ariaLabel="Review mode" layoutId="review-tab" value={tab} onChange={setTab} options={[{
            value: 'applicants',
            label: 'Applicants',
            count: applicants.length
          }, {
            value: 'discover',
            label: 'Discover'
          }, {
            value: 'lineup',
            label: 'Line-up'
          }]} data-id="element-1084" data-mp="nc72zszldlyu" />
            <button type="button" onClick={() => setPrefsOpen(true)} aria-label="Matching preferences" className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-line bg-surface transition-colors duration-150 hover:border-ink-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-1085" data-mp="2y5joxackqxxu">
              <SlidersHorizontalIcon className="h-5 w-5" data-id="element-1086" data-mp="2lmo154ci7cye" />
            </button>
          </div>
        </div>
      </header>

      {tab === 'lineup' ? <div className="mx-auto w-full max-w-6xl px-5 pb-12 pt-2 lg:px-8" data-id="element-1087" data-mp="2qmskr38pknsu">
          <LineupBoard event={event} onDiscover={() => setTab('discover')} data-id="element-1088" data-mp="2cs00jrhvijfx" />
        </div> : <div className="flex min-h-0 flex-1" data-id="element-1089" data-mp="2hlm0qk6uouuu">
          <div className="flex min-w-0 flex-1 flex-col px-5 pb-6 pt-2" style={{
        minHeight: 560
      }} data-id="element-1090" data-mp="22vn2khfapjfd">
            {tab === 'discover' && <p className="mb-3 text-center text-sm text-ink-3" data-id="element-1091" data-mp="27vjb8tyxtz19">Suggested by fit with your preferences. Swipe right to invite.</p>}
            <SwipeDeck key={`${event.id}-${tab}`} className="flex-1" items={deck} getKey={x => x.performer.id} renderCard={x => <PerformerCardFace performer={x.performer} fit={x.fit} travel={travelCost(x.performer, x.performer.distanceKm)} applied={tab === 'applicants'} onDetails={() => isDesktop ? undefined : setSheetId(x.performer.id)} data-id="element-1093" data-mp="1fa42yf8i7xqo" />} onSwipe={(x, dir) => onSwipe(x.performer, dir)} stamps={{
          right: tab === 'applicants' ? 'Match' : 'Invite',
          left: 'Pass'
        }} likeLabel={tab === 'applicants' ? 'Match' : 'Invite'} likeIcon={tab === 'applicants' ? <HeartIcon className="h-7 w-7" fill="currentColor" data-id="element-1094" data-mp="271ab9iydp1r1" /> : <SendIcon className="h-6 w-6" data-id="element-1095" data-mp="22ynd70b46kle" />} onUndo={() => m.undoOrgPass()} canUndo={m.orgPassed.length > 0} empty={<div className="grid h-full place-items-center rounded-lg border-2 border-dashed border-line" data-id="element-1096" data-mp="2byabhd8jraa8">
                  {tab === 'applicants' ? <EmptyState icon={<UsersIcon className="h-7 w-7" data-id="element-1098" data-mp="3jcsos7s3nd0z" />} title="No applicants waiting" body="Invite performers yourself, or feature the event to reach more people." data-id="element-1097" data-mp="3ja6h8857kxir">
                      <Button variant="primary" icon={<SearchIcon className="h-4 w-4" data-id="element-1100" data-mp="3s9bgjgolguvz" />} onClick={() => setTab('discover')} data-id="element-1099" data-mp="3scpk2s8zva50">
                        Discover performers
                      </Button>
                      {!event.featured && <Button variant="outline" onClick={() => setFeaturing(event)} data-id="element-1101" data-mp="2zkw6l8kbl2ri">
                          Feature · ฿490
                        </Button>}
                    </EmptyState> : <EmptyState icon={<SearchIcon className="h-7 w-7" data-id="element-1103" data-mp="3ecepbe4kdbek" />} title="You’ve seen every suggestion" body="Loosen your matching preferences to widen the pool." data-id="element-1102" data-mp="3ecojzf0k06ql">
                      <Button variant="primary" onClick={() => setPrefsOpen(true)} data-id="element-1104" data-mp="2lv2f0ihfk4e3">
                        Adjust preferences
                      </Button>
                    </EmptyState>}
                </div>} data-id="element-1092" data-mp="1tvi8ta30yjjo" />
          </div>
          <aside className="hidden w-[440px] shrink-0 overflow-y-auto border-l border-line px-5 pb-6 pt-1 lg:block" style={{
        maxHeight: 'calc(100dvh - 120px)'
      }} data-id="element-1105" data-mp="29an90gc57ya8">
            {top ? <PerformerDetail key={top.performer.id} performer={top.performer} fit={top.fit} travel={travelCost(top.performer, top.performer.distanceKm)} data-id="element-1106" data-mp="1zeakrxzdacmv" /> : <EmptyState icon={<UsersIcon className="h-7 w-7" data-id="element-1108" data-mp="1lo72fi3lz32p" />} title="Nothing to show" body="The full profile of the top card appears here." data-id="element-1107" data-mp="1lmm55kpwt615" />}
          </aside>
        </div>}

      <Sheet open={Boolean(sheetItem)} onClose={() => setSheetId(null)} title={sheetItem?.performer.name ?? 'Performer'} hideTitle tall data-id="element-1109" data-mp="1bphi7io8p6ja">
        {sheetItem && <PerformerDetail performer={sheetItem.performer} fit={sheetItem.fit} travel={travelCost(sheetItem.performer, sheetItem.performer.distanceKm)} data-id="element-1110" data-mp="17pqlyzfy1y7d" />}
      </Sheet>
      <Sheet open={prefsOpen} onClose={() => setPrefsOpen(false)} title="Matching preferences" data-id="element-1111" data-mp="2pfghxsv64wgl">
        <p className="mb-6 text-[15px] text-ink-2" data-id="element-1112" data-mp="344crxvgscjju">Fit scores and suggestions update as you change these.</p>
        <OrganiserPreferencesForm value={m.organiserPrefs} onChange={m.setOrganiserPrefs} data-id="element-1113" data-mp="2z6kwirgxmfaw" />
      </Sheet>
      <FeatureSheet event={featuring} onClose={() => setFeaturing(null)} data-id="element-1114" data-mp="2bib6fdcor5dy" />
    </div>;
}